/**
 * MobileOptimizationProvider.jsx - Proveedor de Optimización Móvil
 * 
 * Context provider para gestión de experiencia móvil optimizada en todo el sistema grupal:
 * - Detección automática de dispositivos móviles y tablets
 * - Gestión de orientación de pantalla y cambios de viewport
 * - States globales para modo móvil, touch gestures y navegación
 * - Optimización de performance para dispositivos con recursos limitados
 * - Adaptación automática de componentes según capabilities del device
 * 
 * Funcionalidades clave:
 * - Hook useMobile() para consumption en cualquier componente
 * - Detección de touch capability y gesture support  
 * - Manejo de teclado virtual y viewport changes
 * - Cache de preferences móviles en localStorage
 * - Event listeners optimizados para scroll, resize y orientation
 * - Performance monitoring para dispositivos low-end
 * 
 * Optimizaciones específicas:
 * - Lazy loading para componentes pesados en mobile
 * - Throttling de expensive operations 
 * - Simplified UI modes para pantallas pequeñas
 * - Touch-friendly interaction zones (min 44px)
 * - Swipe gestures para navegación between panels
 * 
 * @component
 * @author Sistema EPL - Mobile Optimization
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Crear contexto móvil
const MobileContext = createContext();

// Hook personalizado para usar el contexto móvil
export const useMobile = () => {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobile debe ser usado dentro de MobileOptimizationProvider');
  }
  return context;
};

// Configuración de breakpoints responsivos
const BREAKPOINTS = {
  mobile: 768,      // < 768px es móvil
  tablet: 1024,     // 768px - 1024px es tablet  
  desktop: 1200,    // > 1024px es desktop
  xl: 1440          // > 1440px es desktop grande
};

// Configuración de touch zones mínimas
const TOUCH_ZONES = {
  minTouchTarget: 44,  // Mínimo 44px para targets táctiles
  minSpacing: 8,       // Mínimo 8px entre elementos táctiles
  maxButtonsPerRow: 3  // Máximo 3 botones por fila en móvil
};

export const MobileOptimizationProvider = ({ children }) => {
  // Estados principales del contexto móvil
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  
  // Estados de capabilities del dispositivo
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [hasHover, setHasHover] = useState(false);
  const [devicePixelRatio, setDevicePixelRatio] = useState(1);
  const [connectionSpeed, setConnectionSpeed] = useState('unknown');
  
  // Estados de UI móvil
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Sidebar colapsado por default en móvil
  const [simplifiedMode, setSimplifiedMode] = useState(false);
  const [touchGesturesEnabled, setTouchGesturesEnabled] = useState(true);
  
  // Referencias para performance y eventos
  const resizeTimeoutRef = useRef(null);
  const orientationTimeoutRef = useRef(null);
  const performanceMetricsRef = useRef({
    renderCount: 0,
    lastRenderTime: Date.now()
  });

  /**
   * Detectar características del dispositivo
   */
  const detectDeviceCapabilities = useCallback(() => {
    // Detección de touch
    const touchSupport = 'ontouchstart' in window || 
                        navigator.maxTouchPoints > 0 || 
                        navigator.msMaxTouchPoints > 0;
    setIsTouchDevice(touchSupport);

    // Detección de hover capability
    const hoverSupport = window.matchMedia('(hover: hover)').matches;
    setHasHover(hoverSupport);

    // Device pixel ratio
    setDevicePixelRatio(window.devicePixelRatio || 1);

    // Connection speed (si está disponible)
    if (navigator.connection) {
      const connection = navigator.connection;
      setConnectionSpeed(connection.effectiveType || 'unknown');
    }

    console.log('📱 Device capabilities detected:', {
      touch: touchSupport,
      hover: hoverSupport,
      pixelRatio: window.devicePixelRatio,
      connection: navigator.connection?.effectiveType
    });
  }, []);

  /**
   * Actualizar dimensiones del viewport
   */
  const updateViewport = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setViewportWidth(width);
    setViewportHeight(height);
    
    // Determinar tipo de dispositivo
    const mobile = width < BREAKPOINTS.mobile;
    const tablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.desktop;
    const landscape = width > height;
    
    setIsMobile(mobile);
    setIsTablet(tablet);
    setIsLandscape(landscape);

    // Auto-colapsar sidebar en móvil
    if (mobile) {
      setSidebarCollapsed(true);
    }

    // Modo simplificado para dispositivos muy pequeños
    setSimplifiedMode(width < 360 || height < 600);

    console.log('📏 Viewport updated:', { width, height, mobile, tablet, landscape });
  }, []);

  /**
   * Detectar teclado virtual en móviles
   */
  const detectVirtualKeyboard = useCallback(() => {
    const initialHeight = window.innerHeight;
    let keyboardCheckTimeout;

    const checkKeyboard = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = initialHeight - currentHeight;
      
      // Si la altura se reduce significativamente, probablemente es el teclado
      const keyboardThreshold = isMobile ? 150 : 200;
      const isKeyboardVisible = heightDifference > keyboardThreshold;
      
      if (isKeyboardVisible !== keyboardVisible) {
        setKeyboardVisible(isKeyboardVisible);
        
        // Emit custom event for components that need to know
        window.dispatchEvent(new CustomEvent('keyboardVisibilityChange', {
          detail: { visible: isKeyboardVisible, height: heightDifference }
        }));
      }
    };

    // Throttle keyboard checks
    keyboardCheckTimeout = setTimeout(checkKeyboard, 150);
    return () => clearTimeout(keyboardCheckTimeout);
  }, [isMobile, keyboardVisible]);

  /**
   * Optimizar performance para móviles
   */
  const optimizePerformance = useCallback(() => {
    const metrics = performanceMetricsRef.current;
    metrics.renderCount++;
    
    const now = Date.now();
    const timeSinceLastRender = now - metrics.lastRenderTime;
    
    // Si los renders son muy frecuentes, reducir complejidad
    if (timeSinceLastRender < 16 && metrics.renderCount % 10 === 0) {
      console.warn('🐌 High frequency renders detected, consider optimization');
      
      // Auto-enable simplified mode on struggling devices
      if (isMobile && !simplifiedMode) {
        setSimplifiedMode(true);
        console.log('📱 Auto-enabled simplified mode for performance');
      }
    }
    
    metrics.lastRenderTime = now;
  }, [isMobile, simplifiedMode]);

  /**
   * Gestión de gestos táctiles
   */
  const handleTouchGestures = useCallback((element, options = {}) => {
    if (!isTouchDevice || !touchGesturesEnabled) return null;

    let startX, startY, startTime;
    const threshold = options.threshold || 50;
    const timeThreshold = options.timeThreshold || 300;

    const handlers = {
      touchstart: (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startTime = Date.now();
      },
      
      touchend: (e) => {
        if (!startX || !startY) return;
        
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const endTime = Date.now();
        
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const deltaTime = endTime - startTime;
        
        if (deltaTime > timeThreshold) return; // Too slow to be a swipe
        
        // Detect swipe direction
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          if (Math.abs(deltaX) > threshold) {
            const direction = deltaX > 0 ? 'right' : 'left';
            options.onSwipe && options.onSwipe(direction, deltaX);
          }
        } else {
          if (Math.abs(deltaY) > threshold) {
            const direction = deltaY > 0 ? 'down' : 'up';
            options.onSwipe && options.onSwipe(direction, deltaY);
          }
        }
        
        // Reset
        startX = startY = null;
      }
    };

    // Add event listeners if element provided
    if (element) {
      Object.keys(handlers).forEach(event => {
        element.addEventListener(event, handlers[event], { passive: true });
      });
      
      // Return cleanup function
      return () => {
        Object.keys(handlers).forEach(event => {
          element.removeEventListener(event, handlers[event]);
        });
      };
    }

    return handlers;
  }, [isTouchDevice, touchGesturesEnabled]);

  /**
   * Obtener classes CSS para optimización móvil
   */
  const getMobileClasses = useCallback((baseClasses = '', options = {}) => {
    let classes = baseClasses;
    
    // Mobile-specific classes
    if (isMobile) {
      classes += ' mobile-optimized';
      
      if (options.touchTarget) {
        classes += ' min-h-[44px] min-w-[44px] p-2'; // Touch-friendly sizing
      }
      
      if (options.spacing) {
        classes += ' space-y-2 space-x-2'; // Adequate spacing for touch
      }
      
      if (options.text) {
        classes += ' text-base leading-relaxed'; // Readable text on mobile
      }
    }
    
    // Tablet-specific classes
    if (isTablet) {
      classes += ' tablet-optimized';
    }
    
    // Landscape-specific optimizations
    if (isLandscape && isMobile) {
      classes += ' landscape-mobile';
    }
    
    // Simplified mode classes
    if (simplifiedMode) {
      classes += ' simplified-mode reduced-motion';
    }
    
    // Keyboard visible adjustments
    if (keyboardVisible) {
      classes += ' keyboard-visible';
    }

    return classes.trim();
  }, [isMobile, isTablet, isLandscape, simplifiedMode, keyboardVisible]);

  /**
   * Configuración de componentes para móvil
   */
  const getComponentConfig = useCallback((componentType) => {
    const baseConfig = {
      animations: !simplifiedMode,
      lazyLoad: isMobile,
      touchOptimized: isTouchDevice,
      reducedComplexity: simplifiedMode
    };

    switch (componentType) {
      case 'dashboard':
        return {
          ...baseConfig,
          cardGrid: isMobile ? 1 : isTablet ? 2 : 3,
          showSidebar: !isMobile,
          autoRefresh: !isMobile, // Disable auto-refresh on mobile to save battery
          maxItemsPerPage: isMobile ? 10 : 20
        };
        
      case 'editor':
        return {
          ...baseConfig,
          toolbar: isMobile ? 'compact' : 'full',
          autoSave: true,
          virtualKeyboardSupport: isMobile,
          maxHeight: keyboardVisible ? '40vh' : '60vh'
        };
        
      case 'feedback':
        return {
          ...baseConfig,
          swipeNavigation: isMobile,
          compactMode: isMobile,
          scrollable: true,
          maxCriteriasVisible: isMobile ? 3 : 5
        };
        
      default:
        return baseConfig;
    }
  }, [isMobile, isTablet, isTouchDevice, simplifiedMode, keyboardVisible]);

  /**
   * Setup de event listeners
   */
  useEffect(() => {
    // Detect initial capabilities
    detectDeviceCapabilities();
    updateViewport();

    // Resize handler con throttling
    const handleResize = () => {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        updateViewport();
        detectVirtualKeyboard();
      }, 150);
    };

    // Orientation change handler
    const handleOrientationChange = () => {
      clearTimeout(orientationTimeoutRef.current);
      orientationTimeoutRef.current = setTimeout(() => {
        updateViewport();
        console.log('📱 Orientation changed');
      }, 300);
    };

    // Add event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      clearTimeout(resizeTimeoutRef.current);
      clearTimeout(orientationTimeoutRef.current);
    };
  }, [detectDeviceCapabilities, updateViewport, detectVirtualKeyboard]);

  /**
   * Performance monitoring
   */
  useEffect(() => {
    optimizePerformance();
  }); // Run on every render to monitor performance

  /**
   * Cargar configuraciones guardadas
   */
  useEffect(() => {
    try {
      const savedPrefs = localStorage.getItem('mobile-optimization-prefs');
      if (savedPrefs) {
        const prefs = JSON.parse(savedPrefs);
        setTouchGesturesEnabled(prefs.touchGesturesEnabled ?? true);
        setSimplifiedMode(prefs.simplifiedMode ?? false);
      }
    } catch (error) {
      console.warn('Failed to load mobile preferences:', error);
    }
  }, []);

  /**
   * Guardar configuraciones
   */
  const saveMobilePreferences = useCallback(() => {
    try {
      const prefs = {
        touchGesturesEnabled,
        simplifiedMode,
        lastUpdated: Date.now()
      };
      localStorage.setItem('mobile-optimization-prefs', JSON.stringify(prefs));
    } catch (error) {
      console.warn('Failed to save mobile preferences:', error);
    }
  }, [touchGesturesEnabled, simplifiedMode]);

  // Guardar preferencias cuando cambien
  useEffect(() => {
    saveMobilePreferences();
  }, [saveMobilePreferences]);

  /**
   * Context value con todas las propiedades y funciones
   */
  const contextValue = {
    // Device characteristics
    isMobile,
    isTablet,
    isLandscape,
    isTouchDevice,
    hasHover,
    devicePixelRatio,
    connectionSpeed,
    
    // Viewport information
    viewportWidth,
    viewportHeight,
    keyboardVisible,
    
    // UI states
    sidebarCollapsed,
    setSidebarCollapsed,
    simplifiedMode,
    setSimplifiedMode,
    touchGesturesEnabled,
    setTouchGesturesEnabled,
    
    // Utility functions
    getMobileClasses,
    getComponentConfig,
    handleTouchGestures,
    
    // Constants
    BREAKPOINTS,
    TOUCH_ZONES,
    
    // Performance
    optimizePerformance: () => optimizePerformance()
  };

  return (
    <MobileContext.Provider value={contextValue}>
      {children}
    </MobileContext.Provider>
  );
};

export default MobileOptimizationProvider;